<template>
  <BModal
    :model-value="modelValue"
    @update:model-value="onUpdateModelValue"
    @hide="handleHide"
    hide-footer
    no-footer
    centered
    size="lg"
    :title="modalTitle"
    body-class="p-3"
    no-close-on-backdrop
    no-close-on-esc
    no-header-close
  >
    <!-- Stars -->
    <div class="d-flex align-items-center mb-2">
      <div class="me-2 fw-bold">{{ t('survey.stars') }}</div>

      <div class="survey-stars">
        <button
          v-for="n in 5"
          :key="n"
          type="button"
          class="survey-star btn p-0"
          :class="{ 'is-active': n <= rating }"
          @click="setRating(n)"
        >
          ★
        </button>
      </div>

      <div class="ms-3 text-muted" v-if="rating">
        {{ ratingLabel }}
      </div>
    </div>

    <div class="text-muted small mb-3">
      {{ t('survey.title.optional') }}
    </div>

    <!-- Message for <=4 -->
    <BAlert v-if="rating > 0 && rating <= 4" show variant="secondary" class="small">
      {{ t('survey.text.thankyou') }}
    </BAlert>

    <!-- Reason required for <=4 -->
    <BFormGroup
      v-if="rating > 0 && rating <= 4"
      :label="t('survey.label.telluswhy')"
      label-for="survey-reason"
      class="mb-3"
    >
      <BFormSelect
        id="survey-reason"
        v-model="reasonKey"
        :options="reasonOptions"
        :state="reasonState"
      />
      <BFormInvalidFeedback :state="reasonState">
        {{ t('survey.label.feedbackreason') }}
      </BFormInvalidFeedback>
    </BFormGroup>

    <!-- Comments for any rating -->
    <BFormGroup v-if="rating > 0" :label="t('survey.label.comments')" label-for="survey-comments">
      <BFormTextarea
        id="survey-comments"
        v-model="comments"
        :maxlength="1000"
        rows="5"
        :placeholder="t('survey.placeholder.comments')"
      />
      <div class="d-flex justify-content-end text-muted small mt-1">
        {{ comments.length }} / 1000
      </div>
    </BFormGroup>

    <!-- Phone, Email and Name fields -->
    <div v-if="rating > 0">
      <BFormGroup :label="t('survey.placeholder.emailAddress')" class="mb-3">
        <BFormInput
          v-model.trim="emailAddress"
          @input="userEditedEmail = true"
          @blur="emailBlurred = true"
          type="email"
          maxlength="100"
          :state="emailState"
          :placeholder="t('survey.placeholder.emailAddress')"
        />

        <BFormInvalidFeedback :state="emailState">
          {{ t('survey.error.invalidEmail') }}
        </BFormInvalidFeedback>
      </BFormGroup>
      <BFormGroup :label="t('survey.label.phonenumber')" class="mb-3">
        <div class="d-flex align-items-center">
          <BFormInput
            v-model.trim="phoneNumber"
            @input="userEditedPhone = true"
            type="tel"
            maxlength="25"
            :placeholder="t('survey.placeholder.phonenumber')"
          />
          <BButton
            variant="link"
            class="text-muted ms-2 p-0"
            @click="clearPhone"
            aria-label="Clear phone"
            title="Clear"
          >
            ✕
          </BButton>
        </div>
        <div class="text-muted small mt-1">{{ t('survey.placeholder.phonenumber') }}</div>
      </BFormGroup>

      <BFormGroup :label="t('survey.placeholder.name')" class="mb-3">
        <BFormInput
          v-model.trim="customerName"
          @input="userEditedName = true"
          type="text"
          maxlength="100"
          :placeholder="t('survey.placeholder.name')"
        />
      </BFormGroup>
    </div>

    <BAlert v-if="submitError" show variant="danger" class="small mt-2">
      {{ submitError }}
    </BAlert>

    <div class="d-flex justify-content-between mt-3">
      <BButton variant="outline-secondary" @click="skip" :disabled="submitting">
        {{ t('survey.button.skip') }}
      </BButton>

      <BButton variant="primary" @click="submit" :disabled="!canSubmit">
        <span v-if="submitting">
          <BSpinner small class="me-2" /> {{ t('survey.spinner.submitting') }}
        </span>
        <span v-else>{{ t('survey.button.submit') }}</span>
      </BButton>
    </div>
  </BModal>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import {
  BModal,
  BAlert,
  BFormGroup,
  BFormSelect,
  BFormInvalidFeedback,
  BFormTextarea,
  BFormInput,
  BButton,
  BSpinner
} from 'bootstrap-vue-next'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  workOrderId: { type: Number, required: true }
})

const emit = defineEmits(['update:modelValue', 'submitted', 'skipped'])

const store = useStore()
const { t } = useI18n()

const rating = ref(0)
const reasonKey = ref(null)
const comments = ref('')
const triedSubmit = ref(false)
const submitting = ref(false)
const submitError = ref('')

const emailAddress = ref('')
const phoneNumber = ref('')
const customerName = ref('')

// Track whether the user manually typed so we don't overwrite while modal is open
const userEditedName = ref(false)
const userEditedEmail = ref(false)
const emailBlurred = ref(false)
const userEditedPhone = ref(false)

// Get details of selected user for pre-filling name/phone (if not already edited by user)
const commsIdentity = computed(() => store.getters['auth/communicationIdentity'])

const modalTitle = computed(() => t('survey.title'))

const surveyUserName = computed(() => {
  const src = commsIdentity.value
  if (!src) return ''
  const first = src.firstName || ''
  const last = src.lastName || ''
  return `${first} ${last}`.trim()
})

const surveyUserEmail = computed(() => {
  const src = commsIdentity.value
  return (src && src.email) ? String(src.email) : ''
})

const surveyUserPhone = computed(() => {
  const src = commsIdentity.value
  return (src && src.phone) ? String(src.phone) : ''
})

const ratingLabel = computed(() => {
  switch (rating.value) {
    case 1: return t('survey.rating.inadequate')
    case 2: return t('survey.rating.poor')
    case 3: return t('survey.rating.fair')
    case 4: return t('survey.rating.good')
    case 5: return t('survey.rating.excellent')
    default: return ''
  }
})

const reasonOptions = computed(() => ([
  { value: null, text: t('survey.prompt.selectreason') },
  { value: 'tech_response_time', text: t('survey.option.reason.responsetime') },
  { value: 'connectivity', text: t('survey.option.reason.connectivity') },
  { value: 'could_not_complete', text: t('survey.option.reason.notcomplete') },
  { value: 'technician_communication', text: t('survey.option.reason.communication') },
  { value: 'usability', text: t('survey.option.reason.usability') },
  { value: 'other', text: t('survey.option.reason.other') }
]))

const reasonState = computed(() => {
  if (!(rating.value > 0 && rating.value <= 4)) return null
  if (!triedSubmit.value) return null
  return !!reasonKey.value
})

function isSafeEmail(email) {
  if (!email) return true

  const value = String(email).trim()

  if (/[<>"'`(){}[\]\\;,]/.test(value)) return false

  const emailRegex = /^[A-Za-z0-9](?:[A-Za-z0-9._%+-]{0,62}[A-Za-z0-9])?@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/
  return emailRegex.test(value)
}

const isEmailValid = computed(() => isSafeEmail(emailAddress.value))

const emailState = computed(() => {
  if (!emailAddress.value) return null
  if (!triedSubmit.value && !emailBlurred.value) return null

  return isEmailValid.value
})

const canSubmit = computed(() => {
  if (submitting.value) return false
  if (rating.value === 0) return false
  if (rating.value <= 4 && !reasonKey.value) return false
  if (!isEmailValid.value) return false
  return true
})

function prefillFromIdentity() {
  // Only fill if user hasn't edited yet
  if (!userEditedEmail.value && surveyUserEmail.value) {
    emailAddress.value = surveyUserEmail.value
  }
  if (!userEditedName.value && surveyUserName.value) {
    customerName.value = surveyUserName.value
  }
  if (!userEditedPhone.value && surveyUserPhone.value) {
    phoneNumber.value = surveyUserPhone.value
  }
}

watch(
  [() => props.modelValue, () => commsIdentity.value?.email, surveyUserName, surveyUserPhone],
  ([isOpen]) => {
    if (!isOpen) return

    submitError.value = ''
    triedSubmit.value = false

    // Reset the "user edited" guards each time the modal opens
    // so a new open can prefill again.
    userEditedEmail.value = false
    userEditedName.value = false
    userEditedPhone.value = false
    emailBlurred.value = false

    prefillFromIdentity()
  },
  { immediate: false }
)

watch(
  () => rating.value,
  (n) => {
    if (n === 5) reasonKey.value = null
    triedSubmit.value = false
    submitError.value = ''
  }
)

watch(emailAddress, (val) => {
  if (!val) {
    emailBlurred.value = false
  }
})

function setRating(n) {
  rating.value = n
}

function handleHide() {
  emit('update:modelValue', false)
}

function onUpdateModelValue(v) {
  emit('update:modelValue', v)
}

function clearPhone() {
  phoneNumber.value = ''
  userEditedPhone.value = true
}

function skip() {
  emit('skipped', { workOrderId: props.workOrderId })
  emit('update:modelValue', false)
}

function validate() {
  triedSubmit.value = true
  emailBlurred.value = true

  if (rating.value === 0) return false
  if (rating.value <= 4 && !reasonKey.value) return false
  if (!isEmailValid.value) return false

  return true
}

async function submit() {
  submitError.value = ''
  if (!validate()) return

  submitting.value = true
  try {
    const payload = {
      workOrderId: props.workOrderId,
      feedbackRating: rating.value,
      feedbackReasonKey: rating.value <= 4 ? reasonKey.value : null,
      feedbackComments: comments.value || null,
      feedbackEmailAddress: emailAddress.value || null,
      feedbackPhoneNumber: phoneNumber.value || null,
      feedbackCustomerName: customerName.value || null
    }

    const res = await store.dispatch('clientSurvey/upsert', payload)
    const requestFeedbackId = res && (res.requestFeedbackId || res.RequestFeedbackId)

    emit('submitted', {
      workOrderId: props.workOrderId,
      requestFeedbackId: requestFeedbackId || null,
      rating: rating.value,
      reasonKey: reasonKey.value,
      comments: comments.value,
      emailAddress: emailAddress.value,
      phoneNumber: phoneNumber.value,
      customerName: customerName.value
    })

    emit('update:modelValue', false)
  } catch (e) {
    submitError.value = (e && e.message) ? e.message : t('survey.error.submissionfailed')
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.survey-stars { display: inline-flex; line-height: 1; }
.survey-star {
  font-size: 28px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #cfd4da;
  margin-right: 6px;
}
.survey-star.is-active { color: #0d6efd; }
.survey-star:focus { outline: none; }
</style>
